// Landed cost docs register (G-WebB) — the only web screen for BUILD_SPEC.md
// §5.4/§9.3/§8.1 step 5 (haulage/freight allocation across goods-receipt
// lines); previously reachable only via raw API calls. Reverse-on-POSTED
// mirrors features/purchasing-intake/ReceiptsRegister.tsx's row-action idiom
// (no clone here — a landed cost invoice, unlike a goods receipt/ticket, isn't
// naturally "redone" by cloning; reversing then creating a fresh allocation
// covers the correction case).
import { useMemo, useState } from 'react';
import { Alert, Button, Group, Paper, Select, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconArrowBackUp, IconPlus } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useCanOperate } from '../../auth/permissions';
import type { LandedCostDocResponse, LandedCostDocStatus } from '@zebra/core';
import { ConfirmButton } from '../../components/ConfirmButton';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { PageHeader } from '../../components/PageHeader';
import { LandedCostFormModal } from './LandedCostFormModal';
import { formatMoneyCents } from './format';
import { useLandedCostDocs, useReverseLandedCostDoc, useSuppliers } from './api';

function RowActions({ doc, onOpen }: { doc: LandedCostDocResponse; onOpen: (id: string) => void }) {
  const canOperate = useCanOperate();
  const reverse = useReverseLandedCostDoc(doc.id);
  const [error, setError] = useState<string | null>(null);

  async function handleReverse() {
    setError(null);
    try {
      await reverse.mutateAsync();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to reverse this landed cost doc');
    }
  }

  return (
    <Stack gap={2}>
      <Group gap="xs" wrap="nowrap">
        <Button size="xs" variant="light" onClick={() => onOpen(doc.id)}>
          {doc.status === 'DRAFT' ? 'Continue' : 'View'}
        </Button>
        {doc.status === 'POSTED' && (
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <ConfirmButton
                size="xs"
                color="red"
                variant="light"
                leftSection={<IconArrowBackUp size={14} />}
                loading={reverse.isPending}
                disabled={!canOperate}
                label="Reverse"
                message={`Reverse landed cost allocation ${doc.number}? Its cost will be backed out of every goods receipt line it was allocated to by a reversing ledger entry.`}
                onConfirm={handleReverse}
              />
            </span>
          </Tooltip>
        )}
      </Group>
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
}

export function LandedCostRegister() {
  const [statusFilter, setStatusFilter] = useState<LandedCostDocStatus | null>(null);
  const { data, isLoading, isError } = useLandedCostDocs({ status: statusFilter ?? undefined });
  const { data: suppliers } = useSuppliers();
  const [modalOpened, setModalOpened] = useState(false);
  const [modalDocId, setModalDocId] = useState<string | null>(null);

  const supplierName = useMemo(() => new Map((suppliers?.items ?? []).map((s) => [s.id, s.name])), [suppliers]);
  const rows = useMemo(() => [...(data?.items ?? [])].reverse(), [data]);

  function openCreate() {
    setModalDocId(null);
    setModalOpened(true);
  }
  function openRow(id: string) {
    setModalDocId(id);
    setModalOpened(true);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Landed cost allocations"
        description="Split a haulage or freight invoice across the goods receipts it covers, so each product's cost includes delivery."
        actions={
          <Group gap="sm">
            <Select
              placeholder="All statuses"
              clearable
              data={['DRAFT', 'POSTED', 'REVERSED']}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as LandedCostDocStatus | null)}
              w={160}
            />
            <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
              New allocation
            </Button>
          </Group>
        }
      />

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load landed cost docs.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={800}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Number</Table.Th>
                <Table.Th>Supplier</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th>Lines</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((doc) => (
                <Table.Tr key={doc.id}>
                  <Table.Td>{doc.number}</Table.Td>
                  <Table.Td>{supplierName.get(doc.supplierId) ?? doc.supplierId}</Table.Td>
                  <Table.Td>{doc.description}</Table.Td>
                  <Table.Td>{formatMoneyCents(doc.amountCents)}</Table.Td>
                  <Table.Td>{doc.allocations.length}</Table.Td>
                  <Table.Td>
                    <DocStatusBadge status={doc.status} />
                  </Table.Td>
                  <Table.Td>
                    <RowActions doc={doc} onOpen={openRow} />
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center" py="md">
                      No landed cost allocations yet — use "New allocation" to split a haulage/freight invoice across goods receipt
                      lines.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      {modalOpened && (
        <LandedCostFormModal key={modalDocId ?? 'new'} docId={modalDocId} onClose={() => setModalOpened(false)} />
      )}
    </Stack>
  );
}
